<script setup lang="ts">
import store from "@/store";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { toast } from "@/common/utils/toast";

import {
  methodGroupList,
  methodGroupCreate,
  methodGroupDelete,
} from "@/common/api/rest/method/method-group";

import type {
  MethodGroupType,
  MethodGroupPayloadType,
} from "@/common/types/method/method-group";

import { RegisterTheme } from "@/common/themes";
import {
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";
import MethodGroupForm from "@/components/forms/method-group.vue";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const tableHeader = computed(() => [
  { key: "name", label: t("common.labels.name") },
]);

const tableData = ref<MethodGroupType[]>([]);

const payload = ref<MethodGroupPayloadType | null>(null);
const selected = ref<MethodGroupType | null>(null);

const isLoading = ref(false);
const showModalAdd = ref(false);
const showModalDel = ref(false);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const accountId = computed(() => route.params.id as string);

const delMessage = computed(() => {
  const message = t("method.texts.del_confirm", {
    concat: selected.value?.name,
  });
  const undo = t("common.texts.undo_message");

  return `${message}<br>${undo}`;
});

function fetchData() {
  isLoading.value = true;

  methodGroupList(accountId.value, {
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function add() {
  showModalAdd.value = true;
}

function addCancel() {
  showModalAdd.value = false;
  payload.value = null;
}

function addConfirm() {
  if (!payload.value) return;

  showModalAdd.value = false;
  store.commit("SET_LOADING", true);

  methodGroupCreate(accountId.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        router.push(
          `/${accountId.value}/register/method/${data.group_id}/edit`,
        );
      } else {
        toast({ message: message, type: "error" });
        showModalAdd.value = true;
        fetchData();
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
      showModalAdd.value = true;
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: MethodGroupType) {
  router.push(`/${accountId.value}/register/method/${data.group_id}/edit`);
}

function del(data: MethodGroupType) {
  showModalDel.value = true;
  selected.value = data;
}

function delCancel() {
  showModalDel.value = false;
  selected.value = null;
}

function delConfirm() {
  if (!selected.value) return;

  showModalDel.value = false;
  store.commit("SET_LOADING", true);

  methodGroupDelete(accountId.value, selected.value.group_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        selected.value = null;
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function searchItem(value: string) {
  search.value = value;
  fetchData();
}

onMounted(() => {
  if (accountId.value) fetchData();
});
</script>

<template>
  <register-theme tab-active="method">
    <template #title>
      <svg-icon icon="method" size="2rem" />
      {{ t("common.labels.method_group") }}
    </template>

    <template #content>
      <baseTable
        :header="tableHeader"
        :data="tableData"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :edit="true"
        :del="true"
        @change:page="changePage"
        @searching="searchItem"
        @btn:add="add()"
        @btn:edit="edit"
        @btn:del="del"
      />
    </template>
  </register-theme>

  <base-modal
    :show="showModalAdd"
    :show-button-confirm="true"
    :button-confirm="t('common.labels.confirm')"
    :button-close="t('common.labels.cancel')"
    @close="addCancel()"
    @confirm="addConfirm()"
  >
    <template #content>
      <method-group-form
        :title="t('common.labels.add', { concat: t('common.labels.group') })"
        :data="payload"
        @update-data="payload = $event"
      />
    </template>
  </base-modal>

  <base-confirm
    :show="showModalDel"
    :title="t('method.texts.del_method')"
    :message="delMessage"
    @cancel="delCancel()"
    @confirm="delConfirm()"
  />
</template>
